/**
 * The KnowIt adapter for CANOPY (`canopy.*`, bare JSON — see `canopyTypes.ts`). A 404 from
 * `tree.node`/`tree.open` means "no such node, or not yours" and comes back as `null`; every other
 * failure is thrown, so an outage never renders as an empty tree.
 */
import {
  type CanopyApi,
  CanopyFindResultSchema,
  type CanopyNodeQuery,
  CanopyNodeViewSchema,
  CanopyOpenResultSchema,
  CanopyPerspectiveResultSchema,
  CanopyScopeSchema,
  DebugTreeSchema,
  type KnowledgeDebugApi,
  KnowledgeDebugReportSchema,
} from "~/api/canopyTypes";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}

export interface CanopyTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

function isNotFound(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === 404;
}

function defined(body: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(body).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
}

export function createKnowItCanopy(transport: CanopyTransport): CanopyApi {
  return {
    scopes: async () => {
      const raw = obj(await transport.post("canopy.scopes", {}));
      return (Array.isArray(raw.scopes) ? raw.scopes : []).map((s) => CanopyScopeSchema.parse(s));
    },

    node: async (query: CanopyNodeQuery) => {
      try {
        const raw = await transport.post(
          query.id || query.path || query.scope ? "canopy.tree.node" : "canopy.tree.root",
          defined({ ...query }),
        );
        return CanopyNodeViewSchema.parse(raw);
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },

    find: async (input) =>
      CanopyFindResultSchema.parse(
        await transport.post(
          "canopy.tree.find",
          defined({ query: input.query, scope: input.scope, k: input.k }),
        ),
      ),

    open: async (input) => {
      try {
        return CanopyOpenResultSchema.parse(
          await transport.post(
            "canopy.tree.open",
            defined({ id: input.id, maxTokens: input.maxTokens }),
          ),
        );
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
    perspective: async (input) => {
      try {
        return CanopyPerspectiveResultSchema.parse(
          await transport.post("canopy.perspective", defined({ ...input })),
        );
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  };
}

/** `knowledge.debug` / `knowledge.debug.tree` — admin-only, bare JSON; a 404 tree is "no tree yet". */
export function createKnowItKnowledgeDebug(transport: CanopyTransport): KnowledgeDebugApi {
  return {
    report: async (scope) =>
      KnowledgeDebugReportSchema.parse(await transport.post("knowledge.debug", defined({ scope }))),
    tree: async (input) => {
      try {
        return DebugTreeSchema.parse(
          await transport.post(
            "knowledge.debug.tree",
            defined({ scope: input.scope, nodeId: input.nodeId, depth: input.depth }),
          ),
        );
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  };
}
